import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { UserWorkplaceUpsertDialogStore } from './user-workplace-upsert-dialog.store';
import { UserWorkplaceObject } from '@libs/types/src/user/object-type/workplace/user-workplace.object';
import { workplaceFrequencyEnumOptions } from '@libs/types/src/services/enum/workplace-hours-frequency.enum';
import { fb } from '../../../../../shared/shared.global-service';
import { FormArray, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AddressDetailObject } from '@it2go/types';
import { mapAddress } from '../../../../../shared/helpers/map-address.helper';
import { dateify, dateifyOrNow } from '../../../../../shared/helpers/date.helper';
import {
    UserWorkplaceHoursUpsertInput,
} from '@libs/types/src/user/input-type/workplace/user-workplace-hours.upsert-input';
import { UserWorkplaceUpdateInput } from '@libs/types/src/user/input-type/workplace/user-workplace.update-input';
import { UserWorkplaceCreateInput } from '@libs/types/src/user/input-type/workplace/user-workplace.create-input';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { DividerModule } from '@openng/optimus-ui/divider';
import { ButtonComponent } from '../../../../../../components/kit/button/button.component';
import { DeleteButtonComponent } from '../../../../../../components/kit/button/delete-button.component';
import { TranslatePipe } from '@ngx-translate/core';
import { UserWorkplaceHoursParserPipes } from '../user-workplace-table.pipes';
import { KeyValuePipe } from '@angular/common';
import { BaseDialogComponent } from '../../../../../../components/kit/base-dialog/base-dialog.component';
import { contentGlobalSuccess } from '../../../../content.error';
import { removeTypename } from '../../../../../shared/utils/object.utils';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { CheckInputComponent } from '../../../../../../components/kit/input/check-input/check-input.component';
import { AddressInputComponent } from '../../../../../../components/kit/input/address-input/address-input.component';

@Component({
    selector: 'app-user-workplace-upsert-dialog',
    templateUrl: 'user-workplace-upsert-dialog.component.html',
    styleUrl: 'user-workplace-upsert-dialog.component.scss',
    providers: [UserWorkplaceUpsertDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DialogModule,
        DividerModule,
        ButtonComponent,
        DeleteButtonComponent,
        TranslatePipe,
        UserWorkplaceHoursParserPipes,
        KeyValuePipe,
        ReactiveFormsModule,
        TextInputComponent,
        DateInputComponent,
        SelectInputComponent,
        CheckInputComponent,
        AddressInputComponent,
    ],
})
export class UserWorkplaceUpsertDialogComponent extends BaseDialogComponent {
    public readonly store = inject(UserWorkplaceUpsertDialogStore);

    protected userId: string | null = null;
    protected item: UserWorkplaceObject | null = null;

    protected prefix = 'user.workplace.';

    protected form = fb.group({
        name: fb.control('', Validators.required),
        address: fb.control<AddressDetailObject | null>(null, Validators.required),
        hours: fb.array([]),
        validFrom: fb.control<Date>(new Date(), Validators.required),
        validTo: fb.control<Date | null>(null),
    });

    constructor() {
        super();

        this.userId = this.dialogConfig.data.userId;
        this.item = this.dialogConfig.data.item;

        this.form.controls.hours.clear();

        if (this.item) {
            this.form.patchValue({
                name: this.item.name,
                address: mapAddress(this.item.address),
                validFrom: dateifyOrNow(this.item.validFrom),
                validTo: dateify(this.item.validTo),
            });

            const h: any = {};
            this.item.hours.forEach((it) => {
                if (!(it.type in h)) {
                    h[it.type] = [];
                }
                h[it.type].push(it);
            });
            Object.entries(h).forEach(([type, hours]) => {
                const fg = fb.group({
                    type: fb.control(type, Validators.required),
                    times: fb.array([]),
                });
                this.form.controls.hours.push(fg as any);
                let days = [0, 1, 2, 3, 4, 5, 6];
                (hours as any).forEach((it: any) => {
                    days = days.filter((ii) => ii != it.day);
                    this.addDaysHours(fg, it.day, true, dateify(it.from), dateify(it.to));
                });
                // Fill remaining days
                days.forEach((ii) => {
                    this.addDaysHours(fg, ii);
                });
            });
        } else {
            this.form.patchValue({
                validFrom: new Date(),
            });
        }
    }

    protected addHours(): void {
        const fg = fb.group({
            type: fb.control('', Validators.required),
            times: fb.array([]),
        });
        (this.form.controls.hours as FormArray).push(fg);

        for (let i = 0; i < 7; i++) {
            this.addDaysHours(fg, i);
        }
    }

    protected addDaysHours(fg: FormGroup, _day: number, active: boolean = false, from: Date | null = null, to: Date | null = null): FormGroup {
        const day = _day % 7;
        const arrs = fg.controls.times as FormArray;
        let index = 0;
        for (; index < arrs.length; index++) {
            if (arrs.at(index).value.day > day) {
                break;
            }
        }
        arrs.insert(index, fb.group({
            day: fb.control(day, Validators.required),
            active: fb.control<boolean>(active),
            from: fb.control<Date | null>(from),
            to: fb.control<Date | null>(to),
        }));

        return fg;
    }

    protected deleteHours(index: number): void {
        this.form.controls.hours.removeAt(index);
    }

    protected deleteDaysHours(fa: FormArray, day: number, hourIndex: number): void {
        let index = 0;
        for (; index < fa.length; index++) {
            if (fa.at(index).value.day == day) {
                break;
            }
        }
        const hasNext = fa.at(index + 1)?.value?.day == day;

        if (hasNext) {
            fa.removeAt(index + hourIndex);
        } else {
            fa.at(index + hourIndex).patchValue({ from: null, to: null });
        }
    }

    override save(): void {
        if (this.form.isInvalid()) return;

        const hours: any[] = [];
        this.form.value.hours?.forEach((it: any) => {
            let lastDay = -1;
            let active = true;

            for (const time of it.times) {
                if (lastDay != time.day) {
                    active = time.active;
                    lastDay = time.day;
                }

                if (active && (time.from || time.to)) {
                    hours.push(<UserWorkplaceHoursUpsertInput>{
                        type: it.type,
                        day: Number(time.day),
                        from: time.from?.toISOString(),
                        to: time.to?.toISOString(),
                    });
                }
            }
        });

        const _data: any = {
            ...this.form.value,
            address: removeTypename(this.form.value.address),
            validFrom: this.form.value.validFrom?.toISOString(),
            validTo: this.form.value.validTo?.toISOString(),
            active: true,
            hours,
        };

        if (this.item) {
            const data = _data as UserWorkplaceUpdateInput;
            data.id = this.item.id;
            this.store.api.updateUserWorkplace(data).then(() => {
                contentGlobalSuccess.next('common.updated');
                this.dialogRef.close();
            });

        } else {
            const data = _data as UserWorkplaceCreateInput;
            if (this.userId) {
                data.userId = this.userId;
                this.store.api.createUserWorkplace(data).then(() => {
                    contentGlobalSuccess.next('common.created');
                    this.dialogRef.close({ data: data });
                });
            } else {
                this.dialogRef.close({ data: data });
            }
        }
    }

    protected readonly workplaceFrequencyEnumOptions = workplaceFrequencyEnumOptions;

}
